import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate, duplicateTemplate,
  listPriceCodes, listProjects, listClients, listQuotes, createProject, createQuote,
  applyTemplateLinesToProject, listAutoSources,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import RawNumInput from "../components/measure/RawNumInput";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Bookmark, Trash2, Wand2, ArrowRight, Pencil, Plus, ChevronUp, ChevronDown, ChevronRight, Copy,
  X, Check, FilePlus2, Filter, RotateCcw, FileSpreadsheet, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { UNIT_LABEL } from "../lib/calculator";
import useCollapseMap from "../lib/useCollapseMap";
import TemplateImportModal from "../components/templates/TemplateImportModal";

const CATEGORIES = [
  { key: "all",        label: "Alle" },
  { key: "interior",   label: "Binnen" },
  { key: "exterior",   label: "Buiten" },
  { key: "lacquer",    label: "Lakwerk" },
  { key: "woodwork",   label: "Houtwerk" },
  { key: "decorative", label: "Decoratief" },
  { key: "wallpaper",  label: "Behang" },
  { key: "general",    label: "Extra's" },   // v6 F11.3 — was "Algemeen"; key blijft general voor BC
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label]));

const fmtEUR_legacy = (n) => `€ ${(+n || 0).toFixed(2).replace(".", ",")}`;
const fmtEUR = (n) => {
  // v6 F8 — Belgische notatie met duizendtalseparator: € 1.234,56
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(+n || 0).replace(/\u00A0/g, " ");
  } catch { return fmtEUR_legacy(n); }
};


/**
 * v6 F16 S2 — Auto-bron selector voor één sjabloonregel.
 *
 * Toont enkel bronnen die passen bij de eenheid van de gekoppelde prijscode
 * (strikte matching m² ↔ m², lm ↔ lm, piece ↔ piece). Gebruikersvriendelijke
 * NL-labels (bv. "Actieve deurbladen"). Wanneer geen bron gekozen is, blijft
 * de qty manueel — huidige/legacy werking behouden.
 *
 * Bronnen worden gegroepeerd per scope-type voor een rustig overzicht:
 *   Trap · Zone · Binnenschrijnwerk · Buitenschrijnwerk · Beide · Aantal-objecten
 */
function AutoSourceSelect({ value, pcUnit, autoSources, onChange, testId }) {
  // Filter catalog op unit-match. Sorteer op label (NL alfabetisch).
  const options = useMemo(() => {
    if (!autoSources || Object.keys(autoSources).length === 0) return [];
    const list = Object.entries(autoSources)
      .filter(([, meta]) => meta.unit === pcUnit)
      .map(([key, meta]) => ({
        key,
        label: meta.label,
        scopes: meta.scopes || [],
      }));
    // Groepering: bepaal een groep-label per bron
    const groupOf = (opt) => {
      if (opt.key.startsWith("stair.")) return "Trap";
      if (opt.key.startsWith("zone.")) return "Zone";
      if (opt.key.startsWith("joinery.pieces")) return "Aantal objecten";
      const s = opt.scopes;
      const hasIn = s.includes("interior_joinery");
      const hasOut = s.includes("exterior_joinery");
      if (hasIn && hasOut) return "Beide (binnen + buiten)";
      if (hasIn) return "Binnenschrijnwerk";
      if (hasOut) return "Buitenschrijnwerk";
      return "Overige";
    };
    const grouped = new Map();
    list.forEach((opt) => {
      const g = groupOf(opt);
      if (!grouped.has(g)) grouped.set(g, []);
      grouped.get(g).push(opt);
    });
    // Sort each group's items alphabetically by label
    for (const arr of grouped.values()) {
      arr.sort((a, b) => a.label.localeCompare(b.label, "nl"));
    }
    // Order groups
    const order = ["Trap", "Zone", "Binnenschrijnwerk", "Buitenschrijnwerk",
                   "Beide (binnen + buiten)", "Aantal objecten", "Overige"];
    return order
      .filter((g) => grouped.has(g))
      .map((g) => ({ group: g, items: grouped.get(g) }));
  }, [autoSources, pcUnit]);

  if (options.length === 0) return null;
  const currentLabel = value && autoSources[value] ? autoSources[value].label : "";
  return (
    <div className="mt-1.5 flex items-center gap-1.5" data-testid={`${testId}-container`}>
      <span className="text-[10px] uppercase tracking-wider text-[#5D7259] font-semibold flex-shrink-0">
        Auto:
      </span>
      <Select
        value={value || "__none__"}
        onValueChange={(v) => onChange(v === "__none__" ? "" : v)}
      >
        <SelectTrigger
          className={`h-7 rounded-md text-[11px] px-2 w-full max-w-[280px] ${value ? "bg-[#F2F5EE] text-[#3F5E3F] border-[#6B8E6B]/40 font-semibold" : "bg-white text-[#4F4F4D]"}`}
          data-testid={testId}
          title={value ? `Auto-bron: ${currentLabel}` : "Geen automatische bron — qty blijft manueel"}
        >
          <SelectValue placeholder="— Manueel / geen bron —">
            {value ? currentLabel : "— Manueel / geen bron —"}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="__none__" data-testid={`${testId}-opt-none`}>
            <span className="italic text-[#5D7259]">— Manueel / geen bron —</span>
          </SelectItem>
          {options.map(({ group, items }) => (
            <div key={group} className="pt-1">
              <div className="text-[10px] uppercase tracking-wider text-[#A8A7A0] font-semibold px-2 py-1 border-t border-[#F0EFEA]">
                {group}
              </div>
              {items.map((opt) => (
                <SelectItem
                  key={opt.key}
                  value={opt.key}
                  data-testid={`${testId}-opt-${opt.key}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </div>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}


export default function Templates() {
  const [items, setItems] = useState([]);
  const [priceCodes, setPriceCodes] = useState([]);
  // v6 F16 S2 — Auto-source catalog (voor bron-selector in sjabloon-editor)
  const [autoSources, setAutoSources] = useState({});
  const [filter, setFilter] = useState("all");
  // v6 F11.9 — Gearchiveerde (active=false) templates worden standaard
  // verborgen zodat de gebruikersbibliotheek zuiver blijft. Toggle boven
  // de lijst laat admin ze desgewenst weer tonen.
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [wizardTpl, setWizardTpl] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = () => listTemplates().then(setItems).catch(() => {});
  useEffect(() => {
    refresh();
    listPriceCodes({ active_only: true }).then(setPriceCodes).catch(() => {});
    // v6 F16 S2 — Load auto-source catalog once (statisch, cached)
    listAutoSources().then(setAutoSources).catch(() => {});
  }, []);

  const pcById = useMemo(() => Object.fromEntries(priceCodes.map((p) => [p.id, p])), [priceCodes]);

  const archivedCount = useMemo(() => items.filter((t) => t.active === false).length, [items]);

  const visible = useMemo(() => {
    let base = showArchived ? items : items.filter((t) => t.active !== false);
    if (filter !== "all") {
      base = base.filter((t) => {
        // v6 F11.8 — Multi-category aware. Bekijk categories[] EN legacy
        // category, zodat een template zichtbaar is onder elk van zijn tags.
        const cats = (t.categories && t.categories.length) ? t.categories : [t.category || "general"];
        return cats.includes(filter);
      });
    }
    // v6 F14 §3 — Alfabetisch gesorteerd binnen de actieve filter, zodat de
    // mobiele lijst scanbaarder wordt en dezelfde volgorde behoudt bij een
    // filter-wissel.
    return [...base].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "nl", { sensitivity: "base" })
    );
  }, [items, filter, showArchived]);

  const remove = async (id) => {
    try {
      await deleteTemplate(id);
      toast.success("Sjabloon verwijderd");
      setConfirmDel(null);
      refresh();
    } catch { toast.error("Fout bij verwijderen"); }
  };

  const toggleActive = async (t) => {
    try {
      await updateTemplate(t.id, { ...t, active: !t.active });
      toast.success(t.active ? "Gedeactiveerd" : "Geactiveerd");
      refresh();
    } catch { toast.error("Er ging iets mis"); }
  };

  // v6 — Dupliceer template (server-side copy met "— kopie" suffix).
  // Origineel blijft onaangeroerd; gebruiker kan kopie aanpassen.
  const onDuplicate = async (t) => {
    try {
      const dup = await duplicateTemplate(t.id);
      toast.success(`Kopie aangemaakt: ${dup.name}`);
      refresh();
      // Open de editor automatisch met de kopie zodat user direct kan aanpassen.
      setEditing(dup);
    } catch { toast.error("Dupliceren mislukt"); }
  };

  // v6 F3 — Persisted accordion state per template. Default = collapsed.
  const visibleIds = useMemo(() => visible.map((t) => ({ id: t.id })), [visible]);
  const {
    isCollapsed: isTplCollapsed, toggle: toggleTpl,
    expandAll: expandAllTpls, collapseAll: collapseAllTpls,
  } = useCollapseMap("emma:listCollapsed:templates", visibleIds, true);

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">
            <Bookmark className="inline w-3 h-3 mr-1" /> Sjablonen
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] mt-2 leading-tight">Offerte sjablonen</h1>
          <p className="text-[#4F4F4D] mt-1 max-w-2xl">
            Kies een sjabloon, vink de voorgestelde regels aan of uit, pas eventueel prijzen aan en voeg ze toe aan een project.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="h-12 rounded-xl gap-2 border-[#E5E2DC] hover:border-[#D96C4A]"
            data-testid="import-templates-btn"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Importeer .xlsx</span>
            <span className="sm:hidden">Import</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const token = localStorage.getItem("session_token");
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/templates/export.xlsx`;
              fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
                .then((r) => r.blob())
                .then((blob) => {
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `EmmaMeasure_sjablonen_${new Date().toISOString().slice(0,10)}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(a.href);
                });
            }}
            className="h-12 rounded-xl gap-2 border-[#E5E2DC] hover:border-[#D96C4A]"
            data-testid="export-templates-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">Exporteer .xlsx</span>
            <span className="sm:hidden">Excel</span>
          </Button>
          <Button onClick={() => setEditing({ name: "", category: "general", categories: ["general"], description: "", active: true, template_lines: [] })}
            className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-2" data-testid="new-template-btn">
            <FilePlus2 className="w-4 h-4" /> <span className="whitespace-nowrap">Nieuw sjabloon</span>
          </Button>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="templates-filter">
        {CATEGORIES.map((c) => (
          <button key={c.key}
            onClick={() => setFilter(c.key)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition border ${filter === c.key ? "bg-[#2C2C2A] text-white border-[#2C2C2A]" : "bg-white text-[#2C2C2A] border-[#E5E2DC] hover:border-[#D96C4A]"}`}
            data-testid={`filter-chip-${c.key}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* v6 F14 §3 — Compacte, uitgelijnde toolbar: gearchiveerde-toggle links,
          Alles open/dicht rechts. Op mobiel wraps deze rij netjes. */}
      {(archivedCount > 0 || visible.length > 1) && (
        <div className="flex items-center gap-2 flex-wrap justify-between" data-testid="templates-toolbar">
          <div className="flex-shrink-0">
            {archivedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium transition border ${
                  showArchived
                    ? "bg-[#5D7259] text-white border-[#5D7259]"
                    : "bg-white text-[#5D7259] border-[#E5E2DC] hover:border-[#5D7259]"
                }`}
                data-testid="toggle-archived"
                title="Toon of verberg gearchiveerde sjablonen"
              >
                {showArchived ? "Verberg" : "Toon"} gearchiveerde <span className="opacity-70">({archivedCount})</span>
              </button>
            )}
          </div>
          {visible.length > 1 && (
            <div className="flex items-center gap-1 text-xs whitespace-nowrap" data-testid="templates-bulk-toolbar">
              <button onClick={expandAllTpls} className="text-[#D96C4A] hover:underline px-2 py-1" data-testid="expand-all-templates">Alles open</button>
              <span className="text-[#A8A7A0]">·</span>
              <button onClick={collapseAllTpls} className="text-[#4F4F4D] hover:underline px-2 py-1" data-testid="collapse-all-templates">Alles dicht</button>
            </div>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="emma-card flex flex-col items-center text-center py-16 gap-3">
          <Filter className="w-10 h-10 text-[#D96C4A]" />
          <h2 className="font-heading text-xl font-semibold">Geen sjablonen in deze categorie</h2>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {visible.map((t) => {
            const nLines = (t.template_lines || []).length;
            // v6 F8.1 — Tel hoeveel regels een afwijkende prijs hebben t.o.v.
            // de standaardprijs uit de centrale prijslijst.
            const nOverrides = (t.template_lines || []).filter((ln) => {
              if (ln.unit_price_override === null || ln.unit_price_override === undefined || ln.unit_price_override === "") return false;
              const pc = priceCodes.find((p) => p.id === ln.price_code_id);
              if (!pc) return false;
              return Number(ln.unit_price_override) !== Number(pc.default_price);
            }).length;
            const legacyCount = (t.measurements || []).length;
            const collapsed = isTplCollapsed(t.id);
            // v6 F14 §3 — Compacte info-regel voor ingeklapte mobiele kaart:
            // "Binnen · Lakwerk · 20 regels". Toont max 2 categorieën + totaal
            // regel-aantal. "+N"-tag alleen tonen wanneer er meer categorieën
            // dan 2 zijn (zoals gevraagd: verberg wanneer geen praktische
            // meerwaarde).
            const cats = (t.categories && t.categories.length)
              ? t.categories
              : [t.category || "general"];
            const displayCats = cats.filter((c) => c !== "joinery");
            const shownCats = displayCats.slice(0, 2);
            const extraCats = displayCats.length - shownCats.length;
            const compactCatLabels = shownCats.map((c) => CATEGORY_LABEL[c] || c);
            const compactInfo = [
              ...compactCatLabels,
              extraCats > 0 ? `+${extraCats} categorieën` : null,
              nLines > 0 ? `${nLines} regels` : null,
              nOverrides > 0 ? `${nOverrides} afwijkende ${nOverrides === 1 ? "prijs" : "prijzen"}` : null,
            ].filter(Boolean).join(" · ");
            return (
              <div key={t.id}
                className={`emma-card flex flex-col ${t.active === false ? "opacity-50" : ""}`}
                data-testid={`template-card-${t.id}`}
              >
                <div className="flex items-start justify-between gap-1 mb-2">
                  <button
                    type="button"
                    onClick={() => toggleTpl(t.id)}
                    className="flex-1 min-w-0 text-left -m-1 p-1 rounded-lg hover:bg-[#FAF9F6]"
                    data-testid={`template-toggle-${t.id}`}
                    aria-expanded={!collapsed}
                  >
                    <div className="flex items-start gap-2">
                      {collapsed
                        ? <ChevronRight className="w-4 h-4 mt-1 text-[#5D7259] flex-shrink-0" />
                        : <ChevronDown className="w-4 h-4 mt-1 text-[#5D7259] flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        {collapsed ? (
                          <>
                            {/* v6 F14 §3 — Ingeklapt: naam op ÉÉN regel met
                                ellipsis, gevolgd door één compacte info-regel.
                                Bij openklappen komt de volledige naam terug. */}
                            <h3
                              className="font-heading font-bold text-base sm:text-lg text-[#2C2C2A] leading-tight truncate"
                              title={t.name}
                              data-testid={`template-name-collapsed-${t.id}`}
                            >
                              {t.name}
                              {t.active === false && (
                                <span className="ml-2 text-[10px] font-semibold uppercase text-[#D9534F] tracking-wider">
                                  Inactief
                                </span>
                              )}
                            </h3>
                            <div
                              className="text-xs text-[#4F4F4D] mt-0.5 truncate"
                              data-testid={`template-info-collapsed-${t.id}`}
                            >
                              {compactInfo || "Geen regels"}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {shownCats.map((c) => (
                                <Badge
                                  key={c}
                                  variant="outline"
                                  className="text-xs"
                                  data-testid={`template-card-cat-${t.id}-${c}`}
                                >
                                  {CATEGORY_LABEL[c] || c}
                                </Badge>
                              ))}
                              {extraCats > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs bg-[#FAF9F6] text-[#5D7259]"
                                  title={displayCats.slice(2).map((c) => CATEGORY_LABEL[c] || c).join(", ")}
                                  data-testid={`template-card-cat-more-${t.id}`}
                                >
                                  +{extraCats} categorieën
                                </Badge>
                              )}
                              {nLines > 0 && <Badge variant="outline" className="text-xs">{nLines} regels</Badge>}
                              {nOverrides > 0 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs border-[#D96C4A] text-[#7F4124] bg-[#FBE9DF]"
                                  title="Aantal regels met een sjabloon-prijs die afwijkt van de standaard prijslijst"
                                  data-testid={`template-override-count-${t.id}`}
                                >
                                  {nOverrides} afwijkende {nOverrides === 1 ? "prijs" : "prijzen"}
                                </Badge>
                              )}
                              {t.active === false && <Badge variant="outline" className="text-xs text-[#D9534F] border-[#D9534F]">Inactief</Badge>}
                            </div>
                            {/* v6 UX-fix — Naam mag op mobile 2 regels gebruiken voor leesbaarheid. */}
                            <h3 className="font-heading font-bold text-lg text-[#2C2C2A] leading-snug break-words line-clamp-2" data-testid={`template-name-full-${t.id}`}>{t.name}</h3>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                  {/* v6 F14 §3 — Actieknoppen compacter en visueel gegroepeerd
                      rechts. Gedeelde border-container zodat ze als één blok
                      voelen i.p.v. 3 losse iconen. */}
                  <div
                    className="flex flex-shrink-0 rounded-lg border border-[#E5E2DC] overflow-hidden bg-white"
                    data-testid={`template-actions-${t.id}`}
                  >
                    <button onClick={() => onDuplicate(t)} className="w-8 h-8 hover:bg-[#F0EFEA] flex items-center justify-center border-r border-[#E5E2DC]" data-testid={`duplicate-template-${t.id}`} title="Dupliceer">
                      <Copy className="w-4 h-4 text-[#4F4F4D]" />
                    </button>
                    <button onClick={() => setEditing(t)} className="w-8 h-8 hover:bg-[#F0EFEA] flex items-center justify-center border-r border-[#E5E2DC]" data-testid={`edit-template-${t.id}`} title="Bewerken">
                      <Pencil className="w-4 h-4 text-[#4F4F4D]" />
                    </button>
                    <button onClick={() => setConfirmDel(t)} className="w-8 h-8 hover:bg-red-50 flex items-center justify-center" data-testid={`delete-template-${t.id}`} title="Verwijderen">
                      <Trash2 className="w-4 h-4 text-[#D9534F]" />
                    </button>
                  </div>
                </div>
                {!collapsed && (
                  <>
                    {t.description && <p className="text-sm text-[#4F4F4D] mt-1 line-clamp-3">{t.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {legacyCount > 0 && <Badge variant="outline" className="text-[#5D7259]">{legacyCount} legacy regels</Badge>}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={() => setWizardTpl(t)} disabled={t.active === false || nLines === 0}
                        className="flex-1 h-11 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-2"
                        data-testid={`use-template-${t.id}`}
                      >
                        <Wand2 className="w-4 h-4" /> Gebruik <ArrowRight className="w-4 h-4 ml-auto" />
                      </Button>
                      <Button variant="outline" onClick={() => toggleActive(t)} className="h-11 rounded-xl px-3"
                        data-testid={`toggle-active-${t.id}`}>
                        {t.active === false ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {wizardTpl && (
        <ApplyWizard
          template={wizardTpl}
          pcById={pcById}
          onClose={() => setWizardTpl(null)}
          onApplied={(quote) => { setWizardTpl(null); navigate(`/projecten/${quote.project_id}`); }}
        />
      )}

      {editing && (
        <TemplateEditor
          template={editing}
          priceCodes={priceCodes}
          autoSources={autoSources}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sjabloon verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmDel?.name}" wordt permanent verwijderd.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction className="bg-[#D9534F] hover:bg-[#B23A3A]" onClick={() => remove(confirmDel.id)}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TemplateImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => refresh()}
      />
    </div>
  );
}

// ---------- Apply wizard: checklist + per-line override ----------
function ApplyWizard({ template, pcById, onClose, onApplied }) {
  const [mode, setMode] = useState("existing");
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [chosenProject, setChosenProject] = useState("");
  const [newProjectName, setNewProjectName] = useState(template.name);
  const [chosenClient, setChosenClient] = useState("");
  // v6 C3 — zone picker for the chosen project. Loaded from the latest quote
  // on the project (since work_zones live on quotes, not projects).
  const [projectZones, setProjectZones] = useState([]);
  const [chosenZoneId, setChosenZoneId] = useState("");
  const [targetQuoteId, setTargetQuoteId] = useState(null);
  // v6 E2 — in "existing project" mode, allow user to create a NEW quote on
  // that project instead of appending to the latest existing quote.
  // append    : current behavior — apply template_lines into latest quote (safe, no overwrites)
  // new_quote : create a fresh quote first, then apply (offerteversies, safe)
  const [appendMode, setAppendMode] = useState("append");
  const [selections, setSelections] = useState(() => {
    const out = {};
    for (const ln of template.template_lines || []) {
      out[ln.id] = {
        selected: ln.default_checked !== false,
        quantity: ln.suggested_qty ?? 0,
        unit_price_override: ln.unit_price_override ?? null,
      };
    }
    return out;
  });

  useEffect(() => {
    Promise.all([listProjects(), listClients()]).then(([ps, cs]) => {
      setProjects(ps);
      setClients(cs);
    });
  }, []);

  // v6 C3 — when a project is chosen in existing-mode, fetch its quotes so we
  // can offer the user a zone-picker. We default to the latest quote's zones
  // and append into that quote (target_quote_id).
  useEffect(() => {
    if (mode !== "existing" || !chosenProject) {
      setProjectZones([]); setChosenZoneId(""); setTargetQuoteId(null);
      return;
    }
    listQuotes(chosenProject).then((qs) => {
      const latest = qs[0];
      setTargetQuoteId(latest?.id || null);
      setProjectZones(latest?.work_zones || []);
      setChosenZoneId("");
    }).catch(() => { setProjectZones([]); setTargetQuoteId(null); });
  }, [mode, chosenProject]);

  const setLine = (id, patch) => setSelections((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const totalPreview = (template.template_lines || []).reduce((sum, ln) => {
    const sel = selections[ln.id] || {};
    if (!sel.selected) return sum;
    const pc = pcById[ln.price_code_id];
    if (!pc) return sum;
    const price = sel.unit_price_override !== null && sel.unit_price_override !== undefined && sel.unit_price_override !== ""
      ? +sel.unit_price_override
      : (ln.unit_price_override ?? +pc.default_price);
    return sum + (+sel.quantity || 0) * (price || 0);
  }, 0);

  const submit = async () => {
    const lines = Object.entries(selections).map(([line_id, s]) => ({
      line_id,
      selected: !!s.selected,
      quantity: s.quantity === "" ? null : +s.quantity,
      unit_price_override: s.unit_price_override === "" || s.unit_price_override === null ? null : +s.unit_price_override,
    }));
    if (!lines.some((l) => l.selected)) return toast.error("Selecteer minstens één regel");

    try {
      let projectId = chosenProject;
      if (mode === "new") {
        if (!newProjectName.trim()) return toast.error("Projectnaam is verplicht");
        if (!chosenClient) return toast.error("Selecteer een klant");
        const p = await createProject({ client_id: chosenClient, name: newProjectName.trim() });
        projectId = p.id;
      }
      if (!projectId) return toast.error("Selecteer een project");
      // v6 E2 — when user picked "Nieuwe offerte" mode, create an empty quote
      // first so we don't append into the latest existing quote (safe, no
      // overwrites). The new quote inherits the project's sticky template
      // context automatically since apply-template-lines sets it backend-side.
      let effectiveTargetQuoteId = targetQuoteId;
      if (mode === "existing" && appendMode === "new_quote") {
        const newQuote = await createQuote({
          project_id: projectId,
          title: template.name,
          description: template.description || "",
          rooms: [], work_zones: [], measurements: [], extra_lines: [],
        });
        effectiveTargetQuoteId = newQuote.id;
      }
      // v6 C3 — if a zone is chosen, target the same quote and bind lines to that zone.
      const payload = { lines };
      if (mode === "existing" && chosenZoneId && effectiveTargetQuoteId) {
        payload.zone_id = chosenZoneId;
        payload.target_quote_id = effectiveTargetQuoteId;
      } else if (mode === "existing" && appendMode === "new_quote" && effectiveTargetQuoteId) {
        // No zone picked but a brand-new quote was just created → target it
        payload.target_quote_id = effectiveTargetQuoteId;
      }
      const q = await applyTemplateLinesToProject(projectId, template.id, payload);
      toast.success(
        appendMode === "new_quote" && mode === "existing"
          ? `Nieuwe offerte aangemaakt — ${q.measurements.length} regels`
          : `Sjabloon toegepast — ${q.measurements.length} regels`
      );
      onApplied(q);
    } catch (e) {
      toast.error("Fout bij toepassen sjabloon");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl w-[calc(100vw-2rem)] sm:w-full p-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col gap-0 overflow-hidden"
        data-testid="apply-wizard"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E2DC]">
          <DialogTitle>Sjabloon "{template.name}"</DialogTitle>
          <DialogDescription>Vink de regels aan die je wil meenemen. Pas eventueel hoeveelheid en prijs aan voor dit project.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={mode === "existing" ? "default" : "outline"}
              className={`h-11 rounded-xl ${mode === "existing" ? "bg-[#D96C4A] hover:bg-[#C05A3C]" : ""}`}
              onClick={() => setMode("existing")}
              data-testid="mode-existing-btn"
            >Bestaand project</Button>
            <Button
              variant={mode === "new" ? "default" : "outline"}
              className={`h-11 rounded-xl ${mode === "new" ? "bg-[#D96C4A] hover:bg-[#C05A3C]" : ""}`}
              onClick={() => setMode("new")}
              data-testid="mode-new-btn"
            >Nieuw project</Button>
          </div>
          {mode === "existing" ? (
            <div className="space-y-3">
              <div>
                <Label>Project</Label>
                <Select value={chosenProject} onValueChange={setChosenProject}>
                  <SelectTrigger className="h-12 mt-1.5 rounded-xl" data-testid="wizard-project-select">
                    <SelectValue placeholder="Selecteer project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`wizard-project-option-${p.id}`}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* v6 E2 — append vs new quote mode (only when project selected) */}
              {chosenProject && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="wizard-append-mode">
                  <button
                    onClick={() => setAppendMode("append")}
                    className={`px-3 py-2 rounded-xl border text-left text-xs transition ${appendMode === "append" ? "border-[#D96C4A] bg-[#FFF8F2] text-[#2C2C2A]" : "border-[#E5E2DC] hover:border-[#C9C8C2] text-[#4F4F4D]"}`}
                    data-testid="append-mode-existing-btn"
                  >
                    <div className="font-semibold text-sm">Toevoegen aan bestaande offerte</div>
                    <div className="opacity-80">Regels worden toegevoegd aan de laatste offerte van dit project.</div>
                  </button>
                  <button
                    onClick={() => setAppendMode("new_quote")}
                    className={`px-3 py-2 rounded-xl border text-left text-xs transition ${appendMode === "new_quote" ? "border-[#D96C4A] bg-[#FFF8F2] text-[#2C2C2A]" : "border-[#E5E2DC] hover:border-[#C9C8C2] text-[#4F4F4D]"}`}
                    data-testid="append-mode-new-quote-btn"
                  >
                    <div className="font-semibold text-sm">Maak nieuwe offerte van dit sjabloon</div>
                    <div className="opacity-80">Verse offerte op dit project. Bestaande offertes blijven onaangetast.</div>
                  </button>
                </div>
              )}
              {/* v6 C3 — zone picker, only shown when project has work_zones AND we're appending */}
              {projectZones.length > 0 && appendMode === "append" && (
                <div data-testid="wizard-zone-section">
                  <Label className="flex items-center justify-between">
                    <span>Werkzone (optioneel)</span>
                    <span className="text-[10px] text-[#5D7259] font-normal">{projectZones.length} zone(s) beschikbaar</span>
                  </Label>
                  <Select value={chosenZoneId || "__none__"} onValueChange={(v) => setChosenZoneId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-12 mt-1.5 rounded-xl" data-testid="wizard-zone-select">
                      <SelectValue placeholder="Geen specifieke zone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Geen specifieke zone (algemeen)</SelectItem>
                      {projectZones.map((z) => (
                        <SelectItem key={z.id} value={z.id} data-testid={`wizard-zone-option-${z.id}`}>
                          {z.name} <span className="text-[#5D7259] text-xs ml-1">— {z.zone_type.replace("_", " ")}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {chosenZoneId && (
                    <div className="text-[11px] text-[#5D7259] mt-1 italic">
                      Geselecteerde regels worden toegevoegd aan de bestaande offerte op deze zone.
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Klant</Label>
                <Select value={chosenClient} onValueChange={setChosenClient}>
                  <SelectTrigger className="h-12 mt-1.5 rounded-xl" data-testid="wizard-client-select">
                    <SelectValue placeholder="Selecteer klant" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Projectnaam</Label>
                <Input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                  className="h-12 mt-1.5 rounded-xl" data-testid="wizard-new-project-name" />
              </div>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="mt-5 border-t border-[#E5E2DC] pt-4">
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5D7259] mb-3">Sjabloon-regels</div>
          <div className="space-y-2">
            {(template.template_lines || []).map((ln) => {
              const pc = pcById[ln.price_code_id];
              const sel = selections[ln.id] || {};
              const defaultPrice = ln.unit_price_override ?? (pc ? +pc.default_price : 0);
              const effectivePrice = sel.unit_price_override !== null && sel.unit_price_override !== undefined && sel.unit_price_override !== ""
                ? +sel.unit_price_override
                : defaultPrice;
              const rowTotal = sel.selected ? (+sel.quantity || 0) * effectivePrice : 0;
              return (
                <div key={ln.id}
                  className={`flex flex-wrap items-center gap-2 p-3 rounded-xl border ${sel.selected ? "bg-[#FFF8F2] border-[#D96C4A]/30" : "bg-white border-[#E5E2DC]"}`}
                  data-testid={`wizard-line-${ln.id}`}
                >
                  <button
                    onClick={() => setLine(ln.id, { selected: !sel.selected })}
                    className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition ${sel.selected ? "bg-[#D96C4A] border-[#D96C4A] text-white" : "border-[#C9C8C2]"}`}
                    data-testid={`wizard-check-${ln.id}`}
                    aria-label={sel.selected ? "Deselect" : "Select"}
                  >
                    {sel.selected && <Check className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium text-[#2C2C2A]">
                      {ln.description_override || pc?.description || "—"}
                    </div>
                    <div className="text-[10px] text-[#5D7259] font-mono">
                      {pc?.code || "?"} · {UNIT_LABEL[pc?.unit] || "-"} · default {fmtEUR(defaultPrice)}
                    </div>
                  </div>
                  <RawNumInput
                    value={sel.quantity ?? ""}
                    allowEmpty
                    onChange={(v) => setLine(ln.id, { quantity: v })}
                    disabled={!sel.selected}
                    className="h-9 w-20 rounded-lg text-right font-mono"
                    placeholder="qty"
                    data-testid={`wizard-qty-${ln.id}`}
                  />
                  <RawNumInput
                    value={sel.unit_price_override ?? ""}
                    allowEmpty
                    onChange={(v) => setLine(ln.id, { unit_price_override: v === "" ? null : v })}
                    disabled={!sel.selected}
                    className="h-9 w-24 rounded-lg text-right font-mono"
                    placeholder={fmtEUR(defaultPrice).replace("€ ", "")}
                    data-testid={`wizard-price-${ln.id}`}
                  />
                  <div className="w-24 text-right text-sm font-bold text-[#2C2C2A]" data-testid={`wizard-total-${ln.id}`}>
                    {fmtEUR(rowTotal)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </div>

        <DialogFooter
          className="px-5 py-3 border-t border-[#E5E2DC] bg-white flex-row items-center justify-between gap-3 sm:!justify-between"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="text-sm text-[#4F4F4D]">
            Totaal: <strong className="text-[#2C2C2A]" data-testid="wizard-total-preview">{fmtEUR(totalPreview)}</strong>{" "}
            <span className="text-xs">(excl. BTW)</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="h-12 rounded-xl">Annuleer</Button>
            <Button onClick={submit} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C]" data-testid="wizard-apply-btn">
              Toepassen
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Template editor: rename / activate / lines (add/remove/reorder) ----------
function TemplateEditor({ template, priceCodes, autoSources = {}, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: template.name || "",
    description: template.description || "",
    default_quote_description:
      template.default_quote_description ?? template.description ?? "",
    category: template.category || "general",
    // v6 F11.8 — Multi-category. Fallback naar [category] voor legacy.
    categories: (template.categories && template.categories.length)
      ? [...template.categories]
      : [template.category || "general"],
    active: template.active !== false,
    template_lines: [...(template.template_lines || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
  });
  const [pcPickerOpen, setPcPickerOpen] = useState(false);

  const toggleCategory = (key) => setForm((f) => {
    const cur = new Set(f.categories || []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    // Bewaar minimaal één categorie.
    const arr = [...cur];
    if (arr.length === 0) arr.push("general");
    return { ...f, categories: arr, category: arr[0] };
  });

  const updateLine = (idx, patch) => setForm((f) => {
    const nl = [...f.template_lines];
    nl[idx] = { ...nl[idx], ...patch };
    return { ...f, template_lines: nl };
  });
  const removeLine = (idx) => setForm((f) => ({ ...f, template_lines: f.template_lines.filter((_, i) => i !== idx) }));
  const moveLine = (idx, dir) => setForm((f) => {
    const nl = [...f.template_lines];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= nl.length) return f;
    [nl[idx], nl[tgt]] = [nl[tgt], nl[idx]];
    return { ...f, template_lines: nl.map((x, i) => ({ ...x, order: i })) };
  });
  const addLine = (pc) => {
    const cat = (pc?.work_category || "").trim() || "Overige";
    setOpenCats((s) => {
      if (s.has(cat)) return s;
      const n = new Set(s);
      n.add(cat);
      return n;
    });
    setForm((f) => ({
      ...f,
      template_lines: [...f.template_lines, {
        id: crypto.randomUUID(),
        price_code_id: pc.id,
        description_override: null,
        unit_price_override: null,
        suggested_qty: null,
        default_checked: true,
        order: f.template_lines.length,
      }],
    }));
  };

  // v6 F4 — groepeer template_lines per work_category van de price_code.
  // Behoud absolute index zodat moveLine/updateLine blijven werken.
  const linesByCat = useMemo(() => {
    const groups = new Map();
    form.template_lines.forEach((ln, idx) => {
      const pc = priceCodes.find((p) => p.id === ln.price_code_id);
      const cat = (pc?.work_category || "").trim() || "Overige";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push({ ln, idx, pc });
    });
    // Sort categories alphabetically (S-01 → S-02 → S-03 → S-04 → … → Overige)
    return [...groups.entries()]
      .sort(([a], [b]) => {
        if (a === "Overige") return 1;
        if (b === "Overige") return -1;
        return a.localeCompare(b);
      })
      .map(([cat, items]) => ({
        cat,
        items,
        activeCount: items.filter(({ ln }) => ln.default_checked !== false).length,
      }));
  }, [form.template_lines, priceCodes]);

  const allCats = useMemo(() => linesByCat.map((g) => g.cat), [linesByCat]);

  // v6 F4 — slimme default: categorieën met ≥1 actieve regel staan open.
  // We initialiseren één keer (bij eerste render); daarna controleert de
  // gebruiker zelf wat open/dicht is.
  const [openCats, setOpenCats] = useState(() => {
    const open = new Set();
    const groups = new Map();
    (template.template_lines || []).forEach((ln) => {
      const pc = priceCodes.find((p) => p.id === ln.price_code_id);
      const cat = (pc?.work_category || "").trim() || "Overige";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(ln);
    });
    for (const [cat, items] of groups) {
      if (items.some((ln) => ln.default_checked !== false)) open.add(cat);
    }
    return open;
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    try {
      // Normalize order
      const payload = { ...form, template_lines: form.template_lines.map((l, i) => ({ ...l, order: i })) };
      if (template.id) {
        await updateTemplate(template.id, payload);
        toast.success("Sjabloon bijgewerkt");
      } else {
        await createTemplate(payload);
        toast.success("Sjabloon aangemaakt");
      }
      onSaved();
    } catch (e) {
      // v6 F16 S2 — surface backend auto_source validation errors met NL-message
      const detail = e?.response?.data?.detail;
      if (detail?.errors && Array.isArray(detail.errors)) {
        toast.error(detail.errors[0] || detail.message || "Fout bij opslaan");
      } else {
        toast.error("Fout bij opslaan");
      }
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl w-[calc(100vw-2rem)] sm:w-full p-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col gap-0 overflow-hidden"
        data-testid="template-editor"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E2DC]">
          <DialogTitle>{template.id ? "Sjabloon bewerken" : "Nieuw sjabloon"}</DialogTitle>
          <DialogDescription>Definieer een herbruikbare set offerte-regels.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3">
            <Label>Naam *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-12 mt-1.5 rounded-xl" data-testid="template-name-input" />
          </div>
          <div className="sm:col-span-3">
            <Label>Categorieën <span className="text-[10px] font-normal text-[#5D7259]">(min. 1, meerdere mogelijk)</span></Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="template-categories-chips">
              {CATEGORIES.filter((c) => c.key !== "all").map((c) => {
                const selected = (form.categories || []).includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => toggleCategory(c.key)}
                    className={`h-9 px-3 rounded-full text-xs font-medium border transition ${
                      selected
                        ? "bg-[#2C2C2A] text-white border-[#2C2C2A]"
                        : "bg-white text-[#2C2C2A] border-[#E5E2DC] hover:border-[#D96C4A]"
                    }`}
                    data-testid={`template-cat-chip-${c.key}`}
                    aria-pressed={selected}
                  >
                    {selected && "✓ "}{c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label>Korte beschrijving (intern)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} className="mt-1.5 rounded-xl"
              placeholder="Interne herkenning, vb. 'Binnenschilderwerk standaard 2026'"
              data-testid="template-description-input" />
          </div>
          <div className="sm:col-span-3">
            <Label className="flex items-center gap-2">
              Standaard offerteomschrijving
              <span className="text-xs font-normal text-[#5D7259]">— verschijnt in de offerte-PDF</span>
            </Label>
            <Textarea
              value={form.default_quote_description}
              onChange={(e) => setForm({ ...form, default_quote_description: e.target.value })}
              rows={3} className="mt-1.5 rounded-xl"
              placeholder="Wij voeren de hieronder beschreven werken uit volgens de regels van het vak…"
              data-testid="template-quote-description-input"
            />
            <p className="text-[11px] text-[#5D7259] mt-1">
              Wordt overgenomen op nieuwe offertes die dit sjabloon gebruiken. De
              offerte-omschrijving op project-niveau heeft altijd voorrang.
            </p>
          </div>
          <div className="sm:col-span-3 flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
            <Label className="cursor-pointer">Actief (zichtbaar in lijst)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })}
              data-testid="template-active-switch" />
          </div>
        </div>

        <div className="mt-4 border-t border-[#E5E2DC] pt-3">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5D7259]">
              Regels ({form.template_lines.length})
            </div>
            <div className="flex items-center gap-1.5">
              {form.template_lines.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setOpenCats(new Set(allCats))}
                    className="h-8 rounded-lg px-2 text-[11px] text-[#4F4F4D] hover:text-[#2C2C2A]"
                    data-testid="cats-expand-all"
                  >
                    Alles openen
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setOpenCats(new Set())}
                    className="h-8 rounded-lg px-2 text-[11px] text-[#4F4F4D] hover:text-[#2C2C2A]"
                    data-testid="cats-collapse-all"
                  >
                    Alles sluiten
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setPcPickerOpen(true)} className="h-9 rounded-lg gap-1 text-xs" data-testid="add-line-btn">
                <Plus className="w-3.5 h-3.5" /> Regel toevoegen
              </Button>
            </div>
          </div>

          {form.template_lines.length === 0 && (
            <div className="text-sm text-[#4F4F4D] italic px-3 py-6 text-center border border-dashed border-[#E5E2DC] rounded-xl">
              Geen regels. Voeg er minstens één toe om dit sjabloon bruikbaar te maken.
            </div>
          )}

          {/* v6 F4 — slimme accordion per work_category */}
          <div className="space-y-3">
            {linesByCat.map(({ cat, items, activeCount }) => {
              const isOpen = openCats.has(cat);
              return (
                <div key={cat} className="border border-[#E5E2DC] rounded-xl overflow-hidden bg-white" data-testid={`cat-group-${cat}`}>
                  <button
                    type="button"
                    onClick={() => setOpenCats((s) => {
                      const n = new Set(s);
                      n.has(cat) ? n.delete(cat) : n.add(cat);
                      return n;
                    })}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#FAF9F6] transition"
                    data-testid={`cat-toggle-${cat}`}
                  >
                    <ChevronRight
                      className={`w-4 h-4 text-[#5D7259] transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                    <span className="flex-1 text-left text-sm font-semibold text-[#2C2C2A]">
                      {cat || "Overige"}
                    </span>
                    <span className="text-[11px] font-mono text-[#5D7259]">
                      {activeCount}/{items.length} actief
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-2 pb-2 space-y-2 border-t border-[#F0EFEA]">
                      {items.map(({ ln, idx, pc }) => {
                        // v6 F8.1 — Detecteer of er een afwijkende sjabloon-
                        // prijs actief is en bouw vergelijkings-info op.
                        const hasOverride =
                          ln.unit_price_override !== null &&
                          ln.unit_price_override !== undefined &&
                          ln.unit_price_override !== "" &&
                          pc &&
                          Number(ln.unit_price_override) !== Number(pc.default_price);
                        const overrideTitle = hasOverride
                          ? `Afwijkende sjabloonprijs actief\nStandaard: ${fmtEUR(pc?.default_price || 0)} / ${UNIT_LABEL[pc?.unit] || pc?.unit}\nSjabloon:  ${fmtEUR(ln.unit_price_override)} / ${UNIT_LABEL[pc?.unit] || pc?.unit}`
                          : `Laat leeg om de standaardprijs (${fmtEUR(pc?.default_price || 0)}) te gebruiken`;
                        return (
                        <div
                          key={ln.id}
                          className={`flex flex-wrap items-center gap-2 p-3 rounded-xl border bg-[#FAF9F6] ${hasOverride ? "border-[#D96C4A]" : "border-[#E5E2DC]"}`}
                          data-testid={`editor-line-${idx}`}
                          data-override={hasOverride ? "yes" : "no"}
                        >
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => moveLine(idx, -1)} className="w-6 h-6 rounded hover:bg-white flex items-center justify-center" data-testid={`move-up-${idx}`} aria-label="Omhoog">
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => moveLine(idx, 1)} className="w-6 h-6 rounded hover:bg-white flex items-center justify-center" data-testid={`move-down-${idx}`} aria-label="Omlaag">
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <div className="text-sm font-medium text-[#2C2C2A] flex items-center gap-2 flex-wrap">
                              {ln.description_override || pc?.description || "—"}
                              {hasOverride && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#FBE9DF] text-[#7F4124] border border-[#D96C4A]/40"
                                  data-testid={`override-badge-${idx}`}
                                  title={overrideTitle}
                                >
                                  ⚠ afwijkende sjabloonprijs
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-[#5D7259] font-mono">
                              {pc?.code || "?"} · {UNIT_LABEL[pc?.unit] || "-"} · standaard {fmtEUR(pc?.default_price || 0)}
                            </div>
                            {/* v6 F16 S2 — Auto-bron selector. Alleen bronnen
                                met dezelfde eenheid als deze prijscode zijn
                                zichtbaar. Verborgen voor forfait/uur (die mogen
                                geen auto_source hebben). */}
                            {pc && !["fixed", "hour"].includes(pc.unit) && (
                              <AutoSourceSelect
                                value={ln.auto_source || ""}
                                pcUnit={pc.unit}
                                autoSources={autoSources}
                                onChange={(val) => updateLine(idx, { auto_source: val || null })}
                                testId={`auto-source-${idx}`}
                              />
                            )}
                            {/* v6 F18 — Auto × multiplier. Enkel zichtbaar als
                                er een auto-bron gekozen is. Waarde 1 = default,
                                2 = dubbelzijde (bv. deurblad voor + achter). */}
                            {ln.auto_source && (
                              <div className="flex items-center gap-2 mt-1">
                                <label className="text-[10px] text-[#5D7259] whitespace-nowrap">Auto ×</label>
                                <RawNumInput
                                  value={ln.auto_source_multiplier ?? ""}
                                  allowEmpty
                                  onChange={(v) => updateLine(idx, {
                                    auto_source_multiplier: (v === "" || +v === 1) ? null : +v,
                                  })}
                                  className="h-7 w-14 rounded-md text-right font-mono text-xs"
                                  placeholder="1"
                                  data-testid={`auto-source-mult-${idx}`}
                                />
                                <span className="text-xs text-[#7A7A78]">1 = enkelzijde · 2 = dubbelzijde</span>
                              </div>
                            )}
                          </div>
                          <RawNumInput
                            value={ln.suggested_qty ?? ""}
                            allowEmpty
                            onChange={(v) => updateLine(idx, { suggested_qty: v === "" ? null : v })}
                            className="h-9 w-20 rounded-lg text-right font-mono"
                            placeholder="qty"
                            data-testid={`editor-qty-${idx}`}
                          />
                          <div className="flex items-center gap-1">
                            <RawNumInput
                              value={ln.unit_price_override ?? ""}
                              allowEmpty
                              onChange={(v) => updateLine(idx, { unit_price_override: v === "" ? null : v })}
                              className={`h-9 w-24 rounded-lg text-right font-mono ${hasOverride ? "border-[#D96C4A] bg-[#FFF8EB] text-[#7F4124] font-semibold" : ""}`}
                              placeholder={pc?.default_price != null ? String(pc.default_price) : "prijs"}
                              data-testid={`editor-price-${idx}`}
                              title={overrideTitle}
                            />
                            {hasOverride && (
                              <button
                                type="button"
                                onClick={() => updateLine(idx, { unit_price_override: null })}
                                className="w-7 h-7 rounded-md hover:bg-[#FBE9DF] flex items-center justify-center text-[#7F4124] border border-[#D96C4A]/30"
                                title="Reset naar standaardprijs"
                                aria-label="Reset naar standaardprijs"
                                data-testid={`reset-price-${idx}`}
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <button onClick={() => updateLine(idx, { default_checked: !ln.default_checked })}
                            className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition ${ln.default_checked !== false ? "bg-[#D96C4A] border-[#D96C4A] text-white" : "border-[#C9C8C2]"}`}
                            title="Default aangevinkt in wizard"
                            data-testid={`editor-default-check-${idx}`}
                          >
                            {ln.default_checked !== false && <Check className="w-4 h-4" />}
                          </button>
                          <button onClick={() => removeLine(idx)} className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F]" data-testid={`remove-line-${idx}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>

        <DialogFooter
          className="px-5 py-3 border-t border-[#E5E2DC] bg-white flex-row gap-2 sm:flex-row"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Button variant="outline" onClick={onClose} className="h-12 rounded-xl flex-1 sm:flex-none">Annuleer</Button>
          <Button onClick={save} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] flex-1 sm:flex-none" data-testid="template-save-btn">
            {template.id ? "Bijwerken" : "Aanmaken"}
          </Button>
        </DialogFooter>

        {pcPickerOpen && (
          <PriceCodePicker
            priceCodes={priceCodes}
            onPick={(pc) => { addLine(pc); setPcPickerOpen(false); }}
            onClose={() => setPcPickerOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Price code picker (used inside template editor) ----------
function PriceCodePicker({ priceCodes, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = priceCodes.filter((p) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return p.code.toLowerCase().includes(t) || (p.description || "").toLowerCase().includes(t);
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="pc-picker">
        <DialogHeader>
          <DialogTitle>Prijscode toevoegen</DialogTitle>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek code of omschrijving..."
          className="h-12 rounded-xl" autoFocus data-testid="pc-picker-search" />
        <div className="space-y-1 mt-2">
          {filtered.map((pc) => (
            <button key={pc.id} onClick={() => onPick(pc)}
              className="w-full text-left p-2.5 rounded-lg hover:bg-[#FFF8F2] flex items-center gap-3"
              data-testid={`pc-pick-${pc.code}`}
            >
              <div className="font-mono text-xs text-[#4F4F4D] w-16">{pc.code}</div>
              <div className="flex-1 text-sm text-[#2C2C2A]">{pc.description}</div>
              <div className="text-xs text-[#5D7259]">{UNIT_LABEL[pc.unit]}</div>
              <div className="text-sm font-bold font-mono">{fmtEUR(pc.default_price)}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
